// TestimonialsFloat.js — 3-D floating social-proof field with mouse parallax
// Normal-flow section (no sticky), height: auto, background: #030303
import { useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion';

// ─── Data ─────────────────────────────────────────────────────────────────────
const COMMENTS = [
  { id: 1,  user: '@mrluis_creates',   text: 'bro the Spanish dub sounds exactly like you 😭',              likes: '12.4K', platform: 'yt', depth: 0.9  },
  { id: 2,  user: '@techcreator_sara', text: 'how is this even real? the lip sync is insane',               likes: '3.2K',  platform: 'ig', depth: 0.6  },
  { id: 3,  user: '@global.media.co',  text: 'went from 100K to 340K subscribers in 3 months',             likes: '847',   platform: 'tw', depth: 0.4  },
  { id: 4,  user: '@contentqueen_',    text: 'my Japanese audience finally gets to see my content 🙏',      likes: '5.1K',  platform: 'yt', depth: 1.0  },
  { id: 5,  user: '@studio.berlin',    text: 'replaced our entire dubbing workflow with this',              likes: '2.3K',  platform: 'ig', depth: 0.3  },
  { id: 6,  user: '@travel_with_kai',  text: '32 languages from one recording session... insane',           likes: '9.8K',  platform: 'tw', depth: 0.8  },
  { id: 7,  user: '@natashavcreates',  text: 'my hindi speaking audience doubled in 2 weeks',               likes: '4.4K',  platform: 'yt', depth: 0.5  },
  { id: 8,  user: '@arabictech',       text: 'الصوت يبدو طبيعي جداً. لا أصدق أن هذا AI',                  likes: '6.7K',  platform: 'ig', depth: 0.7  },
  { id: 9,  user: '@jpcreator_yuki',   text: '声がそのままで本当にすごい！',                                likes: '11.2K', platform: 'yt', depth: 1.0  },
  { id: 10, user: '@media_global_',    text: '10 languages in 40 minutes. our old workflow took weeks',     likes: '1.9K',  platform: 'tw', depth: 0.2  },
  { id: 11, user: '@laurenfilms',      text: 'dubbing used to be 3 weeks and $8000. now its 4 minutes',    likes: '22.1K', platform: 'yt', depth: 0.85 },
  { id: 12, user: '@createwithomar',   text: 'the Arabic version gave me chills. it IS my voice',          likes: '8.3K',  platform: 'ig', depth: 0.45 },
];

const POSITIONS = [
  { left: '8%',  top: '12%' },
  { left: '55%', top: '8%'  },
  { left: '30%', top: '22%' },
  { left: '72%', top: '28%' },
  { left: '5%',  top: '42%' },
  { left: '45%', top: '38%' },
  { left: '78%', top: '52%' },
  { left: '18%', top: '62%' },
  { left: '62%', top: '65%' },
  { left: '35%', top: '75%' },
  { left: '85%', top: '18%' },
  { left: '15%', top: '82%' },
];

// Platform badge colours (all monochrome-ish to stay on brand)
const PLATFORM_LABEL = { yt: 'YouTube', ig: 'Instagram', tw: 'Twitter / X' };
const PLATFORM_COLOR = {
  yt: 'rgba(255,255,255,0.18)',
  ig: 'rgba(255,255,255,0.14)',
  tw: 'rgba(255,255,255,0.12)',
};

// ─── Spring config ─────────────────────────────────────────────────────────────
const SPRING_CFG = { stiffness: 60, damping: 22, mass: 0.8 };

// ─── Comment card ──────────────────────────────────────────────────────────────
function CommentCard({ comment, position, index, mouseSpringX, mouseSpringY }) {
  const { depth } = comment;

  // Each card translates by depth * max-offset
  const cardX = useTransform(mouseSpringX, (v) => v * depth * 35);
  const cardY = useTransform(mouseSpringY, (v) => v * depth * 25);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position:  'absolute',
        left:      position.left,
        top:       position.top,
        x:         cardX,
        y:         cardY,
        zIndex:    Math.round(depth * 10),
        maxWidth:  260,
        willChange: 'transform',
      }}
    >
      <Box
        sx={{
          background:    'rgba(255,255,255,0.04)',
          border:        '1px solid rgba(255,255,255,0.08)',
          borderRadius:  '12px',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          padding:       '12px 16px',
          maxWidth:      260,
        }}
      >
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography
            sx={{
              fontSize:   '11px',
              fontWeight: 600,
              color:      'rgba(255,255,255,0.55)',
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '0.01em',
            }}
          >
            {comment.user}
          </Typography>
          <Box
            sx={{
              fontSize:      '9px',
              fontWeight:    600,
              color:         'rgba(255,255,255,0.3)',
              background:    PLATFORM_COLOR[comment.platform],
              borderRadius:  '4px',
              padding:       '2px 5px',
              fontFamily:    'Inter, sans-serif',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {PLATFORM_LABEL[comment.platform]}
          </Box>
        </Box>

        {/* Comment text */}
        <Typography
          sx={{
            fontSize:   '12px',
            lineHeight: 1.6,
            color:      'rgba(255,255,255,0.75)',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            mb:         1,
          }}
        >
          {comment.text}
        </Typography>

        {/* Likes */}
        <Typography
          sx={{
            fontSize:   '10px',
            color:      'rgba(255,255,255,0.25)',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
          }}
        >
          ♥ {comment.likes}
        </Typography>
      </Box>
    </motion.div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function TestimonialsFloat() {
  const sectionRef = useRef(null);

  // Raw mouse motion values (normalised –1 → 1)
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);

  // Smooth spring following
  const mouseSpringX = useSpring(rawX, SPRING_CFG);
  const mouseSpringY = useSpring(rawY, SPRING_CFG);

  const handleMouseMove = useCallback(
    (e) => {
      const rect = sectionRef.current?.getBoundingClientRect();
      if (!rect) return;
      const nx = ((e.clientX - rect.left) / rect.width)  * 2 - 1;
      const ny = ((e.clientY - rect.top)  / rect.height) * 2 - 1;
      rawX.set(nx);
      rawY.set(ny);
    },
    [rawX, rawY]
  );

  const handleMouseLeave = useCallback(() => {
    rawX.set(0);
    rawY.set(0);
  }, [rawX, rawY]);

  return (
    <Box
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        position:   'relative',
        minHeight:  '100vh',
        overflow:   'hidden',
        background: '#030303',
        pt:         { xs: 8, md: 12 },
        pb:         { xs: 8, md: 12 },
      }}
    >
      {/* ── Background giant ghost text ──────────────────────────────────── */}
      {[
        { label: '340% MORE REACH', left: '25%', top: '35%' },
        { label: '3× AUDIENCE',     left: '55%', top: '55%' },
        { label: '+29 MARKETS',     left: '70%', top: '20%' },
      ].map((g) => (
        <Typography
          key={g.label}
          sx={{
            position:      'absolute',
            left:          g.left,
            top:           g.top,
            transform:     'translate(-50%, -50%)',
            fontSize:      'clamp(2rem, 5vw, 4rem)',
            fontWeight:    900,
            color:         'white',
            opacity:       0.04,
            fontFamily:    'Inter, Arial Black, sans-serif',
            letterSpacing: '-0.02em',
            whiteSpace:    'nowrap',
            userSelect:    'none',
            pointerEvents: 'none',
            zIndex:        0,
          }}
        >
          {g.label}
        </Typography>
      ))}

      {/* ── Section header ────────────────────────────────────────────────── */}
      <Box
        sx={{
          position:  'relative',
          zIndex:    2,
          textAlign: 'center',
          mb:        { xs: 6, md: 8 },
          px:        2,
        }}
      >
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <Typography
            sx={{
              fontSize:      '11px',
              fontWeight:    600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color:         'rgba(255,255,255,0.25)',
              fontFamily:    'Inter, sans-serif',
              mb:            2,
            }}
          >
            07 — Social Proof
          </Typography>
        </motion.div>

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
        >
          <Typography
            sx={{
              fontSize:      'clamp(2rem, 5vw, 3.5rem)',
              fontWeight:    700,
              color:         'rgba(255,255,255,0.95)',
              fontFamily:    'Inter, sans-serif',
              letterSpacing: '-0.03em',
              lineHeight:    1.1,
            }}
          >
            Real creators.
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          <Typography
            sx={{
              fontSize:      'clamp(2rem, 5vw, 3.5rem)',
              fontWeight:    700,
              color:         'rgba(255,255,255,0.22)',
              fontFamily:    'Inter, sans-serif',
              letterSpacing: '-0.03em',
              lineHeight:    1.1,
            }}
          >
            Real results.
          </Typography>
        </motion.div>
      </Box>

      {/* ── Floating comment cards ────────────────────────────────────────── */}
      {/* Wrapper that fills remaining space for absolute positioning */}
      <Box
        sx={{
          position: 'relative',
          width:    '100%',
          // enough height to contain the tallest card at bottom-most position (~82% top + card height)
          minHeight: 'calc(100vh - 240px)',
          zIndex:   1,
        }}
      >
        {COMMENTS.map((comment, i) => (
          <CommentCard
            key={comment.id}
            comment={comment}
            position={POSITIONS[i % POSITIONS.length]}
            index={i}
            mouseSpringX={mouseSpringX}
            mouseSpringY={mouseSpringY}
          />
        ))}
      </Box>
    </Box>
  );
}
